import React from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  StepIcon,
} from "@mui/material";
import {
  HelpOutline,
  CheckCircle,
  PlayArrow,
  Upload,
  Edit,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ORDER_ACTION_LABELS } from "../constants/orderActions";
import { ORDER_STEPS, ORDER_ACTION_URLS } from "../constants/orderSteps";
import {
  getFormCompletionPercentage,
  getRequiredForms,
} from "../utils/formStatusMapping";

// Import status utilities from firebaseUtils
import {
  getStatusLabel as getStatusLabelUtil,
  getStatusColor as getStatusColorUtil,
  OrderStatus,
  StatusChangeEntry,
} from "../utils/firebaseUtils";

// Define the shape of an order (adjust fields as needed)
export interface OrderData {
  paypalOrderId: string;
  userId: string;
  status:
    | "pending"
    | "awaiting-info"
    | "in-progress"
    | "completed"
    | "canceled";
  amount: number;
  currency: string;
  createdAt: any; // Firestore Timestamp
  completedAt?: any;
  items: {
    productId: string;
    name: string;
    price: number;
    quantity: number;
    selectedOptions: Record<string, any>;
    description: string;
    image: string;
    requiredForms: string[];
  }[];
  formStatus: Partial<Record<string, boolean>>;
  formData: Record<string, any>;
  actionsRequired: string[];
  shippingAddress?: {
    name: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  notes?: string;
  // Status tracking fields
  statusHistory?: StatusChangeEntry[];
  lastStatusUpdate?: any; // Firestore Timestamp
  lastFormUpdate?: any; // Firestore Timestamp
}

// Props for the OrderCard component
interface OrderCardProps {
  order: OrderData;
  onPrimaryAction?: (orderId: string) => void;
  onSecondaryAction?: (orderId: string) => void;
}

// Map internal status to our theme's status color keys
function mapStatusToColor(
  status: OrderData["status"]
): "warning" | "info" | "success" | "error" {
  return getStatusColorUtil(status as OrderStatus);
}

// Improved status labels
function getStatusLabel(status: OrderData["status"]): string {
  return getStatusLabelUtil(status as OrderStatus);
}

// Get CTA button text and icon based on step
function getStepCTA(stepKey: string): { text: string; icon: React.ReactNode } {
  switch (stepKey) {
    case "Photos":
      return { text: "Upload Photos Now", icon: <Upload /> };
    case "FamilyTree":
      return { text: "Complete Family Tree", icon: <Edit /> };
    case "Missionary":
      return { text: "Fill Missionary Form", icon: <Edit /> };
    case "Generational":
      return { text: "Complete Generational Form", icon: <Edit /> };
    default:
      return { text: "Continue Step", icon: <PlayArrow /> };
  }
}

const humanize = (str: string) =>
  str.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Helper function to format dates in a human-readable way
const formatOrderDate = (createdAt: any): string => {
  if (!createdAt) return "Unknown date";

  try {
    // Handle Firestore Timestamp
    const date = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);

    // Format as "Month Day, Year" (e.g., "December 15, 2024")
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return String(createdAt);
  }
};

// Custom Step Icon component with checkmarks for completed steps
const CustomStepIcon: React.FC<{
  completed: boolean;
  active: boolean;
  icon: React.ReactNode;
}> = ({ completed, active, icon }) => {
  if (completed) {
    return (
      <CheckCircle
        sx={{
          color: "success.main",
          fontSize: 24,
          filter: "drop-shadow(0 2px 4px rgba(76, 175, 80, 0.3))",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        backgroundColor: active ? "secondary.main" : "grey.300",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: active ? "secondary.contrastText" : "grey.600",
        fontSize: 12,
        fontWeight: "bold",
        ...(active && {
          animation: "pulse 2s infinite",
          "@keyframes pulse": {
            "0%": {
              boxShadow: "0 0 0 0 rgba(234, 174, 93, 0.7)",
            },
            "70%": {
              boxShadow: "0 0 0 10px rgba(234, 174, 93, 0)",
            },
            "100%": {
              boxShadow: "0 0 0 0 rgba(234, 174, 93, 0)",
            },
          },
        }),
      }}
    >
      {icon}
    </Box>
  );
};

const OrderCard: React.FC<OrderCardProps> = ({
  order,
  onPrimaryAction,
  onSecondaryAction,
}) => {
  const navigate = useNavigate();
  const firstItem =
    order.items && order.items.length > 0 ? order.items[0] : null;
  const productId = firstItem?.productId || "";
  const steps = ORDER_STEPS[productId] || [];

  // Find the first incomplete step in the correct order
  const currentStepIndex = steps.findIndex((step) =>
    order.actionsRequired.includes(step)
  );
  const activeStep = currentStepIndex === -1 ? steps.length : currentStepIndex;
  const isFullyCompleted = activeStep >= steps.length;

  // Get the next action based on the step order, not the actionsRequired array order
  const nextAction = isFullyCompleted ? "" : steps[activeStep] || "";

  // Get next step CTA
  const nextStepCTA = nextAction ? getStepCTA(nextAction) : null;

  // Handler for clicking a step
  const handleStepClick = (stepKey: string, idx: number) => {
    // Only allow clicking on the current or incomplete steps
    if (idx === activeStep || idx > activeStep) {
      if (onPrimaryAction) {
        onPrimaryAction(`${order.paypalOrderId}:${stepKey}`);
      }
    }
  };

  // Handler for CTA button - navigate to the appropriate URL
  const handleCTAClick = () => {
    if (nextAction) {
      const targetUrl = ORDER_ACTION_URLS[nextAction];
      if (targetUrl) {
        navigate(targetUrl);
      } else if (onPrimaryAction) {
        // Fallback to the original behavior if no URL mapping exists
        onPrimaryAction(`${order.paypalOrderId}:${nextAction}`);
      }
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        border: "1px solid rgba(0, 0, 0, 0.12)",
        ...(isFullyCompleted && {
          backgroundColor: "rgba(46, 139, 87, 0.04)",
        }),
        maxWidth: 400,
        padding: 0,
        flex: "1 1 360px",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={0}
          p={0}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              ORDER #{order.paypalOrderId.toUpperCase()}
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              Placed {formatOrderDate(order.createdAt)}
            </Typography>
          </Box>
          <Chip
            label={getStatusLabel(order.status)}
            size="small"
            sx={{
              fontWeight: "bold",
              backgroundColor: "status.warning",
              color: "white",
              ...(order.status === "awaiting-info" && {
                animation: "glow 2s ease-in-out infinite alternate",
                "@keyframes glow": {
                  from: { boxShadow: "0 0 5px rgba(211, 143, 0, 0.5)" },
                  to: { boxShadow: "0 0 20px rgba(211, 143, 0, 0.8)" },
                },
              }),
            }}
          />
        </Box>

        <Box mt={1}>
          {firstItem ? (
            <>
              <Typography variant="body2" fontWeight="medium" mb={0.5}>
                {firstItem.name || "Unknown Product"}
                {firstItem.selectedOptions?.faces && (
                  <Chip
                    label={`${firstItem.selectedOptions.faces} Faces`}
                    size="small"
                    color="secondary"
                    sx={{ ml: 1, fontSize: "0.7rem" }}
                  />
                )}
              </Typography>

              <Box display="flex" flexWrap="wrap" gap={0.25}>
                {firstItem.selectedOptions &&
                  Object.entries(firstItem.selectedOptions)
                    .filter(([key]) => key !== "faces")
                    .slice(0, 3)
                    .map(([key, value]) => (
                      <Chip
                        key={key}
                        label={`${humanize(key)}: ${value}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.7rem" }}
                      />
                    ))}
                {firstItem.selectedOptions &&
                  Object.keys(firstItem.selectedOptions).length > 4 && (
                    <Chip
                      label={`+${
                        Object.keys(firstItem.selectedOptions).length - 4
                      } more`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem", opacity: 0.7 }}
                    />
                  )}
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Order details loading...
            </Typography>
          )}
        </Box>

        <Box mt={1.5} mb={0.5}>
          <Typography variant="body2" fontWeight="medium" mb={0.5}>
            Progress Steps:
          </Typography>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              "& .MuiStepConnector-root": { top: 10 },
              "& .MuiStepConnector-line": {
                borderColor: "secondary.main",
                borderTopWidth: 1,
              },
              "& .Mui-active .MuiStepConnector-line": {
                borderColor: "secondary.main",
              },
              "& .Mui-completed .MuiStepConnector-line": {
                borderColor: "success.main",
              },
              mt: 0.5,
              mb: 0.5,
            }}
          >
            {steps.map((stepKey, idx) => {
              const isCompleted = idx < activeStep;
              const isActive = idx === activeStep;

              return (
                <Step key={stepKey} completed={isCompleted}>
                  <StepLabel
                    onClick={() => handleStepClick(stepKey, idx)}
                    StepIconComponent={() => (
                      <CustomStepIcon
                        completed={isCompleted}
                        active={isActive}
                        icon={idx + 1}
                      />
                    )}
                    style={{
                      cursor:
                        isActive || idx > activeStep ? "pointer" : "default",
                    }}
                    sx={{
                      "& .MuiStepLabel-label": {
                        fontSize: "0.8rem",
                        fontWeight: isActive ? "bold" : "normal",
                        color: isCompleted
                          ? "success.main"
                          : isActive
                          ? "secondary.main"
                          : "text.secondary",
                      },
                    }}
                  >
                    {stepKey === "FamilyTree"
                      ? "Add Family Info"
                      : stepKey === "Photos"
                      ? "Upload Photos"
                      : ORDER_ACTION_LABELS[stepKey] || humanize(stepKey)}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>

        {isFullyCompleted && (
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              backgroundColor: "success.light",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CheckCircle color="success" />
            <Typography
              variant="body2"
              color="success.dark"
              fontWeight="medium"
            >
              ✅ All steps complete – your order is being processed
            </Typography>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ mt: "auto", px: 2, pb: 2 }}>
        {nextStepCTA && !isFullyCompleted ? (
          <Button
            variant="contained"
            color="secondary"
            startIcon={nextStepCTA.icon}
            endIcon={
              <Tooltip title="Need help with this step?" arrow placement="top">
                <HelpOutline sx={{ fontSize: 18 }} />
              </Tooltip>
            }
            onClick={handleCTAClick}
            size="large"
            sx={{
              width: "100%",
              justifyContent: "center",
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: 2,
              px: 2,
              py: 0.75,
              "&:hover": {
                backgroundColor: "rgba(234, 174, 93, 0.9)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(234, 174, 93, 0.4)",
              },
              transition: "all 0.2s ease",
            }}
          >
            {nextStepCTA.text}
          </Button>
        ) : (
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <Tooltip
              title="Need help with this order? Click for support and FAQ"
              arrow
              placement="top"
            >
              <IconButton
                aria-label="Get Help"
                onClick={() => onSecondaryAction?.(order.paypalOrderId)}
                color="primary"
                sx={{
                  "&:hover": {
                    backgroundColor: "primary.light",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <HelpOutline />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </CardActions>
    </Card>
  );
};

export default OrderCard;
